import {
  BORDER_RADIUS,
  BORDER_RADIUS_LARGE,
  BORDER_RADIUS_SMALL,
  DIVIDER,
  DIVIDER_COLOR,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_LG_WIDTH,
  PAGE_FRAGMENT_SM_WIDTH,
  PAGE_FRAGMENT_SMD_WIDTH,
  PAGE_FRAGMENT_WIDTH,
  PAGE_FRAGMENT_XLG_WIDTH,
  PAGE_FRAGMENT_XXLG_WIDTH,
  PAGE_FRAGMENT_XXXLG_WIDTH,
  PD,
  PD_LG,
  PD_MD,
  PD_SM,
  PD_XLG,
  PD_XSM,
  PD_XXLG,
  PD_XXSM,
  PD_XXXSM,
  SZ_JUMBO,
  SZ_MD,
  SZ_SM,
  SZ_SMSM,
  SZ_SSM,
  SZ_THUMBNAIL,
  SZ_XXLG
} from "./dimens";
import {
  Badge,
  BadgeProps,
  Box,
  BoxProps,
  Breakpoint,
  Button,
  ButtonBase,
  ButtonProps,
  Card,
  CircularProgress,
  IconButton,
  Input,
  InputProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Slider,
  styled,
  SvgIcon,
  TableCell,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, {ReactElement, ReactNode, Ref, useEffect, useRef, useState} from "react";
import {
  Action,
  ActionBase,
  ActionGroup,
  ActionSlider,
  ActionType,
  BADGE_SIZE_KTM,
  BadgeSize,
  EmptyConfig,
  LocalFile,
  Orientation,
  UserProfilePhoto,
  WidgetItem
} from "./types";
import {brightPaletteColors, colorDarkBlue, colorHighlightAlt, colorRed, mediumGray, nightGray, white} from "./colors";
import {Link} from "react-router-dom";
import {DesktopFile} from "./files";
import {createInput, InputSpec} from "./BaseFragment";
import {ICON_BUTTON_LIGHT_XSM_STYLE, ICON_BUTTON_XSM_STYLE_PD} from "./constants";
import {BaseApp} from "./BaseApp";
import {
  BugReportOutlined,
  ChevronRightOutlined,
  ExpandMoreOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  InfoOutlined,
  MoreVertOutlined
} from "@mui/icons-material";
import {md5_code} from "./md5";
import {Member} from "./entities";
import Markdown, {Options} from "react-markdown";
import {App} from "../app/App";
import {findIcon} from "./icons";
import {PathProps} from "./index";

// import defaultImage from "res/images/default_photo_album.png";

export function StyledOrientationComponent(props: {
  breakpoint: Breakpoint,
  render: (orientation: Orientation) => ReactElement
}): ReactElement {
  const theme = useTheme();
  const up = useMediaQuery(theme.breakpoints.up(props.breakpoint));
  return props.render(up ? Orientation.LANDSCAPE : Orientation.PORTRAIT);
}

const VARIANTS = ["h1", "h2", "h3", "h4", "h5", "h6", "body1", "body2", "caption"];

export function StyledHeadingVariant(props: { default_: number, max?: number, children?: any, style?: any }) {
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up("lg"));
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  let n: number;
  if (lg) {
    n = props.default_;
  } else if (sm) {
    n = props.default_ + 1;
  } else {
    n = props.default_ + 2;
  }
  if (!n || n > VARIANTS.length) {
    n = 7;
  } else if (n < 1) {
    n = 1;
  }
  n = Math.min(props.max || VARIANTS.length, n);
  const variant = VARIANTS[n - 1];
  //@ts-ignore
  return <Typography variant={variant}
                     style={props.style}>{props.children}</Typography>;
}


function _StyledMenuBarButton(props: {
  path: PathProps,
  label: string | ReactNode,
  actions: ActionBase[],
  hovering: HTMLElement | null,
  setHovering: (hovering: HTMLElement | null) => void,
  flip?: boolean
}): ReactElement {
  const ref = useRef<HTMLButtonElement>();
  return <Button
    ref={ref}
    style={{fontSize: "90%", fontWeight: "bold", paddingLeft: PD_MD, paddingRight: PD_MD, minWidth: 0}}
    onClick={(event) => {
      // We need to call this after a short timeout to avoid window.onClick from dismissing this newly creating popover.
      setTimeout(() => props.setHovering(event.target as HTMLElement), 1);
    }}
    onMouseEnter={(event) => event.target === ref.current && Boolean(props.hovering) && props.setHovering(event.target as HTMLElement)}
  >
    {props.label}
    <Popover
      anchorEl={props.hovering}
      style={{pointerEvents: "none"}}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      open={props.hovering === ref.current}>
      <StyledActionsListNestedPopover path={props.path} actions={props.actions} flip={props.flip}/>
    </Popover>
  </Button>;
}

export function StyledFullscreenButton(props: ButtonProps & {
  onFullscreenButtonClicked?: (isFullscreen: boolean) => void,
  isFullscreen?: boolean
}): ReactElement {
  return <Tooltip title={!props.isFullscreen ? "Fullscreen" : "Exit fullscreen"}>
    <Button style={{minWidth: 0, width: 24, height: 24, marginRight: PD_SM}}
            onClick={() => props.onFullscreenButtonClicked?.(Boolean(props.isFullscreen))}>
      {!props.isFullscreen ? <FullscreenOutlined/> : <FullscreenExitOutlined/>}
    </Button>
  </Tooltip>;
}

export function StyledFullscreenButtonEx(props: ButtonProps & {
  render: () => ReactElement,
}): ReactElement {
  const [isFullscreen, setFullscreen] = useState<boolean>(false);
  return <StyledFullscreenButton isFullscreen={isFullscreen} onFullscreenButtonClicked={isFullscreen => {
    setFullscreen(isFullscreen);
    BaseApp.CONTEXT.setFullscreenElement(isFullscreen ? props.render() : null);
  }}/>;
}

export function StyledMenuBar(props: { path: PathProps, actionGroups: ActionGroup[], flip?: boolean }): ReactElement {
  const [hovering, setHovering] = useState<HTMLElement>(null);
  const isHoveringRef = useRef<boolean>();
  isHoveringRef.current = Boolean(hovering);
  useEffect(() => {
    const close = () => {
      if (isHoveringRef.current) {
        setHovering(null);
      }
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);
  return <StyledBoxRow style={{gap: 0}}>
    {props.actionGroups.map(group =>
      <_StyledMenuBarButton
        path={props.path}
        flip={props.flip}
        label={group.label}
        hovering={hovering}
        setHovering={setHovering}
        actions={group.actions}/>)}
  </StyledBoxRow>;
}

export function StyledMenuGroupButton(props: {
  path: PathProps,
  actionGroup: ActionGroup,
  flip?: boolean
}): ReactElement {
  const [hoverEl, setHoverEl] = useState<HTMLElement>(null);
  const [subMenuOpen, setSubMenuOpen] = useState<boolean>(false);
  const group = props.actionGroup;
  return <MenuItem
    onMouseEnter={(event) => setHoverEl(event.target as HTMLElement)}
    onMouseLeave={(event) => setTimeout(() => setHoverEl(null), 100)}
  >
    {group.iconType
      ? <ListItemIcon>
        {<props.actionGroup.iconType/>}
      </ListItemIcon>
      : null}
    <Box style={{flexGrow: 1, display: "flex"}}>
      <ListItemText
        sx={{my: 0}}
        inset={!Boolean(group.iconType)}>
        {group.label}
      </ListItemText>
      <ChevronRightOutlined/>
    </Box>
    <Popover
      anchorEl={hoverEl}
      style={{pointerEvents: "none"}}
      anchorOrigin={{
        vertical: 'top',
        horizontal: props.flip ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: props.flip ? 'right' : 'left',
      }}
      onClose={() => setHoverEl(null)}
      open={Boolean(hoverEl) || subMenuOpen}>
      <StyledActionsListNestedPopover path={props.path} actions={props.actionGroup.actions} flip={props.flip}
                                      setSubMenuOpen={setSubMenuOpen}/>
    </Popover>
  </MenuItem>;
}

export function StyledActionsListPopover(props: {
  path: PathProps,
  actions: ActionBase[],
  flip?: boolean
}): ReactElement {
  return <StyledActionsListNestedPopover path={props.path} actions={props.actions} flip={props.flip}/>;
}

function StyledActionsListNestedPopover(props: {
  path: PathProps,
  actions: ActionBase[],
  flip?: boolean, // Flip submenus horizontally (useful when parent menu is aligned to the right, and we want nested popups on the left).
  setSubMenuOpen?: (open: boolean) => void
}) {
  return <MenuList style={{pointerEvents: "auto", minWidth: SZ_XXLG}}
                   onMouseEnter={(event) => props.setSubMenuOpen?.(true)}
                   onMouseLeave={(event) => props.setSubMenuOpen?.(false)}
  >
    {props.actions.map(action => {
      if (action.isHiddenFn?.()) {
        return null;
      }
      switch (action.type) {
        case ActionType.SEPARATOR:
          return <StyledHorizontalDivider/>;
        case ActionType.GROUP:
          const group = action as ActionGroup;
          return <StyledMenuGroupButton path={props.path} actionGroup={group} flip={props.flip}/>;
        case ActionType.BUTTON:
          const button = action as Action;
          return <MenuItem
            onClick={(event) => {
              App.CONTEXT.hidePopover();
              button.onClick?.(event, props.path);
            }}>
            {button.iconType
              ? <ListItemIcon
                style={{color: button.destructive ? colorRed : null}}>
                {<button.iconType/>}
              </ListItemIcon>
              : null}
            <ListItemText
              sx={{my: 0}}
              inset={!Boolean(button.iconType)}
              style={{color: button.destructive ? colorRed : null}}>
              {button.text}
            </ListItemText>
            {button.keyboardShortcut
              ? <Typography sx={{color: "text.secondary"}} style={{marginLeft: PD_MD}}>
                {button.keyboardShortcut}
              </Typography>
              : null}
          </MenuItem>;
      }
      return null;
    })}
  </MenuList>;
}

export function StyledSpan(props: { style?: any }) {
  return <span style={{flexGrow: 1, ...props.style}}/>
}

const STYLED_AVATAR_SIZES = new Map<string, number>([
  ["xlg", 72],
  ["lg", 64],
  ["md", 48],
  ["sm", 24],
  ["xsm", 16],
]);

export function StyledAvatar(props: {
  member: Member,
  defaultImage?: string,
  size?: "xlg" | "lg" | "md" | "sm" | "xsm"
}) {
  const size = STYLED_AVATAR_SIZES.get(props.size || "md");
  return <Card style={{width: size, height: size, flexShrink: 0}}>
    <img style={{width: "100%", height: "100%"}} src={UserProfilePhoto(props.member?.user, props.defaultImage)}/>
  </Card>;
}

export function StyledVisible(props: { visible: boolean, style?: any, children: any }) {
  return <Box style={{display: props.visible ? "flex" : "none", ...props.style}}>
    {props.children}
  </Box>;
}

export function StyledSwatch(props: { color: string, onClick?: (color: string) => void }) {
  return <Button disabled={!Boolean(props.onClick)} style={{width: SZ_SM, height: SZ_SSM, backgroundColor: props.color}}
                 onClick={() => props.onClick(props.color)}/>
}

export function StyledDeferred(props: { fallback: ReactElement, defer: () => Promise<ReactNode> }) {
  const [deferred, setDeferred] = useState(undefined);
  useEffect(() => {
    props.defer().then(rendered => setDeferred(rendered));
  }, []);
  return deferred === undefined
    ? props.fallback
    : deferred;
}

export function StyledVerticalDivider(props: { lineWidth?: number }) {
  const color = BaseApp.CONTEXT.getAppConfig().theme.palette.divider || DIVIDER_COLOR;
  return <div
    style={{
      width: props.lineWidth || 1,
      marginLeft: PD_SM,
      marginRight: PD_SM,
      backgroundColor: color,
      alignSelf: "stretch"
    }}/>;
}

export function StyledHorizontalDivider(props: { lineWidth?: number }) {
  const color = BaseApp.CONTEXT.getAppConfig().theme.palette.divider || DIVIDER_COLOR;
  return <div
    style={{
      height: props.lineWidth || 1,
      marginLeft: PD_SM,
      marginRight: PD_SM,
      backgroundColor: color,
      alignSelf: "stretch"
    }}/>;
}

export function StyledButton(props: ButtonProps & { style?: any }) {
  return <Button {...props} style={{padding: `${PD_XSM}px ${PD_SM}px`, ...props.style}}/>;
}

export function StyledInput(props: InputProps & {}) {
  return <Input {...props} />;//onFocus={() => BaseApp.CONTEXT.showSoftKeyboard()}/>
}

export function StyledBoxRow(props: BoxProps) {
  return <Box style={{display: "flex", gap: PD_SM, ...props.style}}>
    {props.children}
  </Box>;
}

export function StyledBoxRowWithLabel(props: { label: string } & BoxProps) {
  return <StyledBoxColumn>
    <Typography variant="body2" style={{fontWeight: "bold"}}>{props.label}</Typography>
    <Box id={props.id} style={{display: "flex", gap: PD_SM, ...props.style}}>
      {props.children}
    </Box>
  </StyledBoxColumn>;
}

export function StyledBoxColumnWithLabel(props: { boxStyle?: any, label: string } & BoxProps) {
  return <StyledBoxColumn style={{...props.boxStyle}}>
    <Typography variant="body2">{props.label}</Typography>
    <Box id={props.id} style={{display: "flex", flexDirection: "column", gap: PD_SM, ...props.style}}>
      {props.children}
    </Box>
  </StyledBoxColumn>;
}

export function StyledBoxColumnWithBackgroundCoverImage(props: {
  backgroundCoverImage?: string,
  backgroundCoverImageOpacity?: number
} & BoxProps) {
  return <StyledBoxColumn style={{position: "relative", ...props.style}}>
    {props.backgroundCoverImage
      ? <img
        style={{
          zIndex: 0,
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: props.backgroundCoverImageOpacity || 1,
        }}
        src={props.backgroundCoverImage}/>
      : null}
    {props.children}
  </StyledBoxColumn>;
}

export function StyledBoxColumn(props: BoxProps & { gutter?: boolean }) {
  return <Box
    {...props}
    style={{
      display: "flex",
      flexDirection: "column",
      gap: PD_MD,
      paddingLeft: props.gutter ? PD_MD : 0,
      paddingRight: props.gutter ? PD_MD : 0,
      position: "relative",
      ...props.style
    }}/>;
}

export function StyledBoxColumnCard(props: BoxProps & {
  title?: string,
  titleComponent?: ReactElement,
  iconType?: typeof SvgIcon,
  text?: string,
  toolbar?: ReactElement,
  highlight?: boolean,
  highlightColor?: string
}) {
  const theme = useTheme();
  let style = {};
  if (props.highlight || props.highlightColor) {
    style = {
      ...style,
      borderTop: (props.highlightColor || theme.palette.primary.main) + " 4px solid",
    }
  }
  return <Card style={style}>
    <Box style={{
      display: "flex",
      flexDirection: "column",
      gap: PD_SM,
      ...props.style
    }}>
      {props.iconType || props.title || props.text || props.titleComponent
        ? <StyledBoxColumn style={{borderBottom: DIVIDER}}>
          <StyledBoxRow style={{alignItems: "center", paddingLeft: PD_SM, paddingRight: PD_SM, height: SZ_SMSM}}>
            {props.iconType ? <props.iconType/> : null}
            {Boolean(props.titleComponent)
              ? <Box style={{display: "flex", flexGrow: 1}}>
                {props.titleComponent}
              </Box>
              : null}
            {props.title
              ? <>
                <Typography variant="h6">{props.title}</Typography>
                <StyledSpan/>
              </>
              : null}
            {props.toolbar}
          </StyledBoxRow>
          {props.text
            ? <Typography style={{color: nightGray, paddingLeft: PD_SM, paddingRight: PD_SM}}>{props.text}</Typography>
            : null}
        </StyledBoxColumn>
        : null}
      <StyledBoxColumn style={{flexGrow: 1, marginTop: -PD_SM}}>
        {props.children}
      </StyledBoxColumn>
    </Box>
  </Card>;

}

const STYLED_WIDGET_HEIGHT_MAP = new Map<string, number>([
  ["lg", 480],
  ["md", 360],
  ["sm", 240],
  ["xsm", 120],
]);

export function StyledWidget(props: {
  path: PathProps,
  size?: "lg" | "md" | "sm" | "xsm",
  item: WidgetItem
}): ReactElement {
  const theme = useTheme();
  const item = props.item;
  let toolbar: ReactElement;
  if (item.actions?.length > 0) {
    toolbar = <StyledBoxRow>
      <StyledSpan/>
      <IconButton onClick={(event) => {
        BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, item.actions, null, props.path);
      }}>
        <ExpandMoreOutlined/>
      </IconButton>
    </StyledBoxRow>;
  }
  let content: ReactElement;
  if (item.links?.length > 0) {
    content = <>{item.links.map(link => <StyledLink to={link.to}>{link.text}</StyledLink>)}</>;
  } else if (!(content = item.render?.(props.path))) {
    content = <StyledEmpty emptyConfig={item.emptyConfig || {
      title: item.title,
      iconType: item.iconType,
    }}/>;
  }

  return <StyledBoxColumnCard
    // titleComponent={
    //   <StyledBoxRow style={{
    //     background: theme.palette.primary.main,
    //     color: theme.palette.primary.contrastText,
    //     height: SZ_SSM,
    //     marginTop: 0,
    //     alignItems: "center",
    //     paddingLeft: PD_LG,
    //     paddingRight: PD_LG,
    //     borderBottomRightRadius: BORDER_RADIUS_MEDIUM,
    //     borderBottomLeftRadius: 0,
    //     marginLeft: -8
    //   }}>
    //     <item.iconType/>
    //     <Typography>{item.title}</Ty pography>
    //   </StyledBoxRow>}
    iconType={item.iconType}
    title={item.title}
    highlight
    highlightColor={item.highlightColor}
    toolbar={toolbar}>
    <StyledBoxColumn style={{height: STYLED_WIDGET_HEIGHT_MAP.get(props.size || "md"), overflowY: "scroll"}}>
      {content}
    </StyledBoxColumn>
  </StyledBoxColumnCard>;

}

export function StyledTaggedText(props: TypographyProps & {}) {
  const text = props.children as string;
  if (!text || !(typeof text === "string")) {
    return null;
  }
  let transformed: ReactElement[] = [];
  let start, end = -1;
  while ((start = text.indexOf("[", end + 1)) >= 0) {
    transformed.push(<>{text.substring(end + 1, start)}</>);
    if ((end = text.indexOf("]", start + 1)) > 0) {
      const substring = text.substring(start + 1, end);
      transformed.push(<span style={{
        color: brightPaletteColors[md5_code(substring) % brightPaletteColors.length],
        fontWeight: "bold"
      }}>{" " + substring}</span>);
    } else break;
  }
  transformed.push(<>{text.substring(end + 1)}</>);
  return <Typography {...props}>
    {transformed}
  </Typography>;
}

export function StyledEditableTypography(props: TypographyProps & {
  textRef?: Ref<HTMLParagraphElement>,
  boxStyle?: any,
  placeholder?: string,
  onTextInputConfirmed?: (event, text: string) => void,
  onContentEdited?: (event, text: string) => void,
}) {
  const [contentText, setContentText] = useState(props.children as string || "");
  // TODO: Figure out how to set div height to be equal to child placeholder or text element (dependeing on variant).
  return <StyledBoxRow style={{minHeight: "2rem", ...props.boxStyle}}>
    <StyledBoxRow style={{alignItems: "center", position: "relative", flexGrow: 1}}>
      {!props.children && contentText.length <= 0
        ? <Typography
          {...props}
          style={{
            pointerEvents: "none",
            color: mediumGray,
            position: "absolute",
          }}>{props.placeholder}</Typography>
        : null}
      <Typography
        {...props}
        ref={props.textRef}
        contentEditable
        onKeyDown={event => {
          if (event.keyCode === 13) {
            event.preventDefault();
            props.onTextInputConfirmed?.(event, contentText);
            event.currentTarget.blur();
          }
        }}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          right: 0,
          verticalAlign: "middle",
          lineHeight: "2rem",
          ...props.style
        }}
        onBlur={event => props.onContentEdited?.(event, contentText)}
        onInput={event => setContentText(event.currentTarget.innerText)}
      >{props.children}</Typography>
    </StyledBoxRow>
  </StyledBoxRow>;
}

const STYLED_CONTAINER_SIZES = new Map<string, number>([
  ["xxxlg", PAGE_FRAGMENT_XXXLG_WIDTH],
  ["xxlg", PAGE_FRAGMENT_XXLG_WIDTH],
  ["xlg", PAGE_FRAGMENT_XLG_WIDTH],
  ["lg", PAGE_FRAGMENT_LG_WIDTH],
  ["md", PAGE_FRAGMENT_WIDTH],
  ["smd", PAGE_FRAGMENT_SMD_WIDTH],
  ["sm", PAGE_FRAGMENT_SM_WIDTH],
  ["xsm", PAGE_FRAGMENT_HALF_WIDTH],
]);

export function StyledContainer(props: BoxProps & {
  size?: "xxxlg" | "xxlg" | "xlg" | "lg" | "md" | "smd" | "sm" | "xsm"
}) {
  return <StyledBoxColumn style={{
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flexGrow: 1,
    maxWidth: STYLED_CONTAINER_SIZES.get(props.size) || PAGE_FRAGMENT_WIDTH,
    marginLeft: "auto",
    marginRight: "auto",
  }}>
    <StyledBoxColumn className={props.className} style={{padding: PD_SM, ...props.style}}>
      {props.children}
    </StyledBoxColumn>
  </StyledBoxColumn>;
}

export function StyledHeaderContainer(props: {
  title: string,
  text: string,
  image?: string,
  imageStyle?: any,
  headerStyle?: any,
  children: any
}): ReactElement {
  const theme = useTheme();
  return <StyledBoxColumn style={{gap: PD_LG, background: theme.palette.background.paper, flexGrow: 1}}>
    <StyledBoxRow style={{padding: PD_XLG, ...props.headerStyle}}>
      <StyledBoxColumn style={{flexGrow: 1}}>
        <Typography variant="h4">{props.title}</Typography>
        <StyledMarkdown>{props.text}</StyledMarkdown>
      </StyledBoxColumn>
      {props.image
        ? <img src={props.image} style={{width: "25%", maxWidth: SZ_XXLG, flexShrink: 0, ...props.imageStyle}}/>
        : null}
    </StyledBoxRow>
    {props.children}
  </StyledBoxColumn>
}

export function StyledSplitPaneColumn(props: BoxProps & { showSplitBorder?: boolean }) {
  return <div style={{display: "flex", flexDirection: "column", flexGrow: 1, gap: 0, ...props.style}}>
    {(props.children as ReactElement[])
      .map((child, index) =>
        Boolean(child)
          ? <div style={{
            display: "flex",
            flexDirection: "column",
            flexBasis: 0,
            flexGrow: 1,
            borderTop: (props.showSplitBorder && index > 0) ? DIVIDER : null,
          }}>{child}</div>
          : null)
    }
  </div>
}

export function StyledSplitPaneRow(props: BoxProps & { showSplitBorder?: boolean }) {
  return <div style={{display: "flex", flexGrow: 1, gap: 0, ...props.style}}>
    {(props.children as ReactElement[])
      .map((child, index) =>
        <div style={{
          display: "flex",
          flexDirection: "column",
          flexBasis: 0,
          flexGrow: 1,
          borderLeft: (props.showSplitBorder && index > 0) ? DIVIDER : null,
        }}>{child}</div>)
    }
  </div>
}

export function StyledTableHeaderCell(props: { style?: any, children: any }): ReactElement {
  return <TableCell style={{height: SZ_SMSM, paddingLeft: PD_MD, ...props.style}}><b>{props.children}</b></TableCell>;
}

export function StyledTableBodyCell(props: {
  style?: any,
  children: any,
  variant?: "h5" | "h6" | "body1"
}): ReactElement {
  return <TableCell style={{
    paddingLeft: PD_MD,
    height: SZ_SM,
    ...props.style,
  }}>
    <Typography
      variant={props.variant}
      style={{
        overflowX: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        paddingRight: PD_MD,
      }}>
      {props.children}
    </Typography>
  </TableCell>;
}

export function StyledLink(props: { to: string, children: any }) {
  return <Typography><Link to={props.to}>
    {props.children}
  </Link></Typography>;
}

const MORE_BUTTON_SIZE_MAP = new Map<string, number>([
  ["lg", 72],
  ["md", 56],
  ["sm", 48],
  ["xsm", 24],
]);


export function StyledButtonMore(props: { size?: "xsm" | "sm" | "md" | "lg", style?: any, actions: Action[] }) {
  const size = MORE_BUTTON_SIZE_MAP.get(props.size || "md");
  return <IconButton
    style={{width: size, height: size, ...props.style}}
    onMouseDown={event => {
      event.stopPropagation();
      event.preventDefault();
    }}
    onClick={event => {
      event.stopPropagation();
      event.preventDefault();
      BaseApp.CONTEXT.showActions(event.currentTarget, null, props.actions);
    }}>
    <MoreVertOutlined/>
  </IconButton>;
}

export function StyledButtonDebug(props: { size?: "sm" | "md" | "lg", style?: any, actions: Action[] }) {
  const size = MORE_BUTTON_SIZE_MAP.get(props.size || "md");
  return <IconButton
    style={{width: size, height: size, ...props.style}}
    onMouseDown={event => {
      event.stopPropagation();
    }}
    onClick={event => {
      event.preventDefault();
      BaseApp.CONTEXT.showActions(event.currentTarget, null, props.actions);
    }}>
    <BugReportOutlined/>
  </IconButton>;
}

export const StyledBadge = styled(Badge)(({theme}) => ({
  '& .MuiBadge-badge': {
    right: PD_SM,
    top: PD_SM,
    background: colorRed,
    color: white,
    border: `1px solid ${theme.palette.background.paper}`,
  },
}));

function _BouncyBadgeDot(props: { text?: string }): ReactElement {
  const theme = useTheme();
  const [bounce, setBounce] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<any>();
  useEffect(() => {
    if (!props.text) {
      setBounce(false);
      return;
    }
    setBounce(true);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => {
      setBounce(false);
    }, 10000);
    setTimeoutId(id);
  }, [props.text]);
  if (!props.text) {
    return null;
  }
  return <div
    className={bounce ? "animation-bounce2" : null}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "absolute",
      top: 0,
      right: PD_SM,
      background: colorRed,
      color: white,
      paddingLeft: 4,
      paddingRight: 4,
      borderRadius: 24,
      border: `1px solid ${theme.palette.background.paper}`,
    }}>
    <Typography style={{
      fontSize: "75%",
    }}>
      {props.text}
    </Typography>
  </div>
}

export function StyledBouncyBadge(props: BadgeProps & {}): ReactElement {
  return <Badge
    {...props}
    slots={{
      badge: _BouncyBadgeDot,
    }}
    slotProps={{
      badge: {
        // @ts-ignore
        text: props.badgeContent,
      },
    }}
    invisible={!Boolean(props.badgeContent)}
  />;
}

export function StyledBadge2(props: TypographyProps & { size?: BadgeSize, selected?: boolean, boxStyle?: any }) {
  const size = BADGE_SIZE_KTM.findMetadata(props.size || BadgeSize.LARGE);
  return <StyledBoxRow style={{
    background: props.selected ? colorDarkBlue : null,
    color: props.selected ? "white" : null,
    width: size.size,
    height: size.size,
    borderRadius: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...props.boxStyle,
  }}>
    <Typography {...props} style={{fontSize: size.fontSize, ...props.style}}/>
  </StyledBoxRow>
}

export function StyledPleaseWait(): ReactElement {
  return <Box
    style={{display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "center"}}>
    <Box style={{display: "flex", flexDirection: "column", alignItems: "center", gap: PD_SM}}>
      <CircularProgress/>
      <Typography>Please wait...</Typography>
    </Box>
  </Box>;
}

type StyledUploadButtonProps = ButtonProps & {
  style?: any,
  mimeTypes: string[],
  uploadFile: (file: DesktopFile, parentId?: string) => Promise<any>,
}

type StyledUploadButtonState = {
  status: "none" | "uploading" | "done" | "error",
}

export class StyledUploadButton extends React.Component<StyledUploadButtonProps, StyledUploadButtonState> {

  private readonly inputSpec: InputSpec;

  constructor(props: StyledUploadButtonProps, context: any) {
    super(props, context);
    this.state = {
      status: "none",
    }
    this.inputSpec = {
      ref: React.createRef(),
      type: "file",
      multiple: false,
      accept: this.props.mimeTypes?.join(",") || "*",
      onChange: event => {
        event.preventDefault();
        let file = event.target.files[0];
        if (!file) {
          this.setState({
            status: "error",
          });
          return;
        }
        this.setState({
          status: "uploading",
        });
        this.props.uploadFile(new DesktopFile(new LocalFile(file, file.name, file.size)))
          .then((response) => {
            this.setState({
              status: "done",
            });
          })
          .catch(reason => {
            this.setState({
              status: "error",
            });
          })
        //this.listener.onPickImage(files[0]);
      }
    };
  }

  render() {
    return <Box style={{
      display: "flex",
      minHeight: SZ_SM,
    }}>
      {this.renderContent()}
    </Box>;
  }

  private renderContent() {
    let style = this.props.style || {};
    switch (this.state.status) {
      case "none": {
        const input = createInput(this.inputSpec);
        return <Button
          {...this.props}
          onClick={() => this.inputSpec.ref.current.click()}
        >
          {input}
          Upload
        </Button>
      }
      case "uploading":
        return <Box style={{
          display: "flex",
          gap: PD_SM,
          alignItems: "center"
        }}>
          <CircularProgress/>
          <Typography>Uploading...</Typography>
        </Box>;
      case "done":
        return <Box style={{
          display: "flex",
          gap: PD_SM,
          alignItems: "center"
        }}>
          <Typography>Done</Typography>
        </Box>;
      case "error":
        return <Box style={{
          display: "flex",
          gap: PD_SM,
          alignItems: "center"
        }}>
          <Typography>Error</Typography>
        </Box>;
    }
  }
}

export function StyledGroupButton(props: { selected?: boolean } & ButtonProps) {
  return <Button
    style={{borderRadius: BORDER_RADIUS_LARGE, fontSize: "90%", paddingLeft: 16, paddingRight: 16, gap: PD_SM}}
    variant={props.selected ? "contained" : "outlined"}
    {...props}
  />
}

export type StyledListItemProps<T> = {
  disabled?: boolean,
  defaultImage?: string,
  path?: string,
  object?: T,
  variant?: "text" | "default" | "thumbnail",
  size?: "lg" | "md" | "sm",
  selected?: boolean,
  startDecorator?: ReactElement,
  endDecorator?: ReactElement,
  img?: string,
  icon?: typeof SvgIcon,
  iconify?: string | ReactElement,
  iconForegroundColor?: string,
  iconBackgroundColor?: string,
  title: string,
  text?: string,
  titleSecondary?: string,
  textSecondary?: string,
  accessory?: ReactElement,
  noLink?: boolean,
  onClick?: (object: T) => void,
  style?: any,
}

const LIST_ITEM_THUMBNAIL_SIZE_MAP = new Map<string, number>([
  ["lg", SZ_JUMBO * 1.5],
  ["md", SZ_THUMBNAIL],
  ["sm", SZ_XXLG],
]);

export function StyledListItem<T>(props: StyledListItemProps<T>) {
  const theme = useTheme();
  switch (props.variant || "default") {
    case "default":
      return <ButtonBase
        disabled={props.disabled}
        to={props.path}
        onClick={!Boolean(props.path) ? () => props.onClick?.(props.object) : null}
        component={props.noLink || !props.path ? null : Link}>
        <Card style={{
          width: "100%",
          display: "flex",
          padding: PD_MD,
          background: props.selected ? colorHighlightAlt : null,
          color: props.selected ? "black" : null,
          ...props.style
        }}>
          <Box style={{
            width: "100%",
            height: SZ_SM,
            display: "flex",
            gap: PD_MD,
            textTransform: "none",
            alignItems: "center",
          }}>
            {React.isValidElement(props.iconify)
              ? props.iconify
              : (props.icon ?
                <Box style={{
                  width: SZ_SM,
                  height: SZ_SM,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: props.iconBackgroundColor || theme.palette.primary.main,
                  borderRadius: BORDER_RADIUS
                }}>
                  <props.icon
                    style={{color: props.iconForegroundColor || theme.palette.primary.contrastText, fontSize: 28}}/>
                </Box>
                : ((props.img || props.defaultImage) ?
                  <Card style={{width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
                    <img src={props.img || props.defaultImage}
                         onError={event => {
                           const img = event.target as HTMLImageElement;
                           if (props.defaultImage && img.src !== props.defaultImage) {
                             img.src = props.defaultImage;
                           }
                         }}
                         style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                  </Card>
                  : props.startDecorator))
            }
            <Box style={{
              display: "flex", flexDirection: "column", flexGrow: 1, whiteSpace: "nowrap", overflow: "hidden",
            }}>
              <Box style={{display: "flex", width: "100%", alignItems: "center", gap: PD_XXSM}}>
                <Typography style={{
                  fontWeight: "bold",
                  textAlign: "start",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flexGrow: 1,
                }}>{props.title}</Typography>
                {props.titleSecondary ?
                  <Typography
                    variant={"body2"}
                    style={{
                      flexGrow: 0,
                      flexShrink: 0,
                    }}>{props.titleSecondary}</Typography>
                  : null}
              </Box>
              <Box style={{display: "flex", width: "100%", alignItems: "center", gap: PD_XXSM}}>
                <Typography style={{
                  textAlign: "start",
                  overflow: "hidden",
                  flexWrap: "nowrap",
                  textOverflow: "ellipsis"
                }}>{props.text}</Typography>
              </Box>
            </Box>
          </Box>
          <Box style={{display: "flex", alignItems: "center", alignSelf: "center"}}>
            {props.endDecorator}
            {props.accessory}
          </Box>
        </Card>
      </ButtonBase>
    case "thumbnail":
      const size = LIST_ITEM_THUMBNAIL_SIZE_MAP.get(props.size || "md");
      return <Box style={{display: "flex", justifyContent: "center"}}>
        <Button
          color="secondary"
          variant="text"
          style={{
            borderRadius: BORDER_RADIUS_SMALL,
            width: size,
            height: size,
            display: "flex",
            flexDirection: "column",
            gap: PD_SM,
            textTransform: "none",
            alignItems: "center",
            position: "relative",
          }}
          onClick={() => props.onClick?.(props.object as T)}>
          <Card style={{
            position: "absolute",
            borderRadius: BORDER_RADIUS_SMALL,
            left: 0,
            top: 0,
            width: "100%",
            height: "100%"
          }}>
            <img src={props.img} style={{width: "100%", height: "100%", objectFit: "cover"}}/>
          </Card>
          <Box style={{position: "absolute", right: 0, top: 0}}>
            {props.accessory}
          </Box>
          <Box style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            borderColor: props.selected ? colorHighlightAlt : null,
            borderStyle: props.selected ? "solid" : null,
            borderWidth: props.selected ? 8 : 0,
            borderRadius: BORDER_RADIUS_SMALL,
          }}/>
        </Button>
      </Box>;
  }
  return null;
}

export function StyledActionSlider(props: { action: ActionSlider, initialValue?: number, args: any[] }): ReactElement {
  const action = props.action;
  const [value, setValue] = useState<number>(props.initialValue || 0);
  return <div style={{display: "flex", flexDirection: "column"}}>
    <Typography variant="caption">{action.tooltipText}</Typography>
    <StyledBoxRow style={{alignItems: "center", gap: PD_MD}}>
        <action.iconType/>
      <Slider
        size={"small"}
        min={0}
        max={100}
        value={value}
        onChange={(event, value: number) => {
          setValue(value);
          action.onClick?.(event, value, ...props.args);
        }}/>
      <Typography>{Math.floor(value)}</Typography>
    </StyledBoxRow>
  </div>;
}

export function StyledEmpty(props: { emptyConfig: EmptyConfig, style?: any, imageStyle?: any }) {
  let emptyConfig = props.emptyConfig;
  if (!emptyConfig) {
    return null;
  }
  let IconType = emptyConfig.iconType;
  if (!IconType && typeof emptyConfig.iconify === "string") {
    IconType = findIcon(emptyConfig.iconify);
  }
  let style = props.style || {};
  return <Box style={{
    display: "flex",
    flexDirection: "column",
    color: mediumGray,
    background: null,//lightGray,
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    padding: PD_XXLG,
    ...style,
  }}>
    <StyledBoxColumn style={{
      maxWidth: 400,
      alignItems: "center",
    }}>
      {Boolean(IconType)
        ? <IconType style={{fontSize: 72}}/>
        : null}
      {emptyConfig.iconUrl
        ? <img src={emptyConfig.iconUrl} style={{...props.imageStyle}}/>
        : null}
      {React.isValidElement(emptyConfig.iconify)
        ? emptyConfig.iconify
        : null}
      <Typography variant="h5" style={{marginTop: PD_SM, textAlign: "center"}}>{emptyConfig.title}</Typography>
      {Boolean(emptyConfig.text) ?
        <Typography style={{marginTop: PD_SM, textAlign: "center"}}>{emptyConfig.text}</Typography>
        : null}
      <Box style={{display: "flex", alignItems: "center", gap: PD_SM}}>
        {Boolean(emptyConfig.action) ?
          <Button
            startIcon={emptyConfig.action.iconType ? <emptyConfig.action.iconType/> : null}
            variant="contained"
            style={{marginTop: PD_MD}}
            onClick={(event) => emptyConfig.action.onClick(event)}>
            {emptyConfig.action.text}
          </Button>
          : null}
        {Boolean(emptyConfig.altAction) ? <Button
            style={{marginTop: PD_MD}}
            onClick={(event) => emptyConfig.altAction.onClick(event)}>{emptyConfig.altAction.text}</Button>
          : null}
      </Box>
    </StyledBoxColumn>
  </Box>;
}

export function StyledUnselected(props: { unselectedConfig: EmptyConfig, style?: any }) {
  return <StyledEmpty emptyConfig={props.unselectedConfig} style={props.style}/>;
}


export class StyledVerticalStack extends React.PureComponent<{
  label?: string,
  required?: boolean,
  tooltip?: string,
  gap?: string | boolean,
  gutterLabel?: boolean,
  style?: any,
  children?: any
}> {
  render() {
    return <Box style={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: PD_XSM,
      marginTop: this.props.gap ? (typeof this.props.gap === "string" ? PD[this.props.gap] : (Boolean(this.props.gap) ? PD_SM : 0)) : 0,
      ...this.props.style,
    }}>
      {this.props.label || this.props.required
        ? <StyledBoxRow style={{alignItems: "center"}}>
          {this.props.label
            ? <Typography
              style={{
                display: "block",
                fontWeight: "bold",
                marginLeft: this.props.gutterLabel ? PD_SM : 0,
                marginBottom: PD_XXSM,
              }}>
              {this.props.label}
            </Typography>
            : null}
          {this.props.required
            ? <Typography style={{fontWeight: "bold", color: "red", marginLeft: -PD_XSM}}><sup>*</sup></Typography>
            : null}
          {this.props.tooltip
            ? <Tooltip title={this.props.tooltip}>
              <InfoOutlined style={{fontSize: "100%"}}/>
            </Tooltip>
            : null}
        </StyledBoxRow>
        : null}
      {this.props.children}
    </Box>;
  }
}

export class StyledHorizontalStack extends React.PureComponent<{
  label?: string,
  gap?: string | boolean,
  gutterLabel?: boolean,
  style?: any,
  children?: any
}> {
  render() {
    let style = this.props.style || {};
    return <Box style={{
      ...style,
      width: "100%",
      height: SZ_SM,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: PD_SM,
      marginTop: this.props.gap ? (typeof this.props.gap === "string" ? PD[this.props.gap] : (Boolean(this.props.gap) ? PD_SM : 0)) : 0,
    }}>
      {this.props.label ? <Box style={{
          display: "flex",
          minWidth: SZ_MD,
          flexGrow: 0,
          flexShrink: 0,
          justifyContent: "flex-end",
        }}>
          <Typography style={{fontWeight: "bold",}}>
            {this.props.label}
          </Typography>
        </Box>
        : null}
      <Box style={{
        flexGrow: 1,
      }}>
        {this.props.children}
      </Box>
    </Box>;
  }
}

export class StyledToolBox extends React.PureComponent<{ children: any, title: string, actions?: Action[] }> {

  render() {
    return <Card style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      // borderRadius: BORDER_RADIUS,
      // border: DIVIDER
    }}>
      <Box style={{display: "flex", flexDirection: "row", padding: PD_XSM, marginBottom: -8, gap: PD_XXSM}}>
        <Typography style={{marginLeft: PD_XXSM, fontWeight: "bold"}}>{this.props.title}</Typography>
        <span style={{flexGrow: 1}}/>
        {this.props.actions?.map(action => {
          const IconType = action.iconType;
          return <Tooltip title={action.text} arrow>
            <IconButton onClick={(event) => action.onClick(event)}>
              <IconType style={{padding: PD_XXXSM}}/>
            </IconButton>
          </Tooltip>;
        })}
      </Box>
      {this.props.children}
    </Card>
  }
}

export class StyledToolstripTool {
  constructor(readonly name: string, readonly iconType: typeof SvgIcon, readonly hidden?: boolean) {
  }
}

type StyledToolstripProps = {
  orientation?: "horizontal" | "vertical",
  floating?: boolean,
  tools: StyledToolstripTool[],
  initialSelectedToolIndex: number,
  onSelectedToolChanged: (tool) => void,
}

type StyledToolstripState = {
  selectedTool: string,
}

export class StyledToolstrip extends React.PureComponent<StyledToolstripProps, StyledToolstripState> {

  constructor(props: StyledToolstripProps, context: any) {
    super(props, context);
    this.state = {
      selectedTool: this.props.tools[this.props.initialSelectedToolIndex].name,
    };
  }

  componentDidMount() {
    this.props.onSelectedToolChanged(this.state.selectedTool);
  }

  render() {
    return this.props.orientation === "vertical" ? this.renderVertical() : this.renderHorizontal();
  }

  private renderHorizontal() {
    if (this.props.floating) {
      return <Box style={{
        display: "flex",
        flexDirection: "row",
        flexGrow: 1,
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1200,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}>
        {this.renderHorizontalInternal()}
      </Box>;
    } else {
      return this.renderHorizontalInternal();
    }
  }

  private renderHorizontalInternal() {
    return <ToggleButtonGroup
      value={this.state.selectedTool}
      exclusive
      onChange={(e, value) => {
        this.setState({
          selectedTool: value,
        });
        this.props.onSelectedToolChanged(value);
      }}
      style={{margin: PD_SM, height: SZ_SM}}
    >
      {this.props.tools.filter(tool => !tool.hidden).map(tool => this.createToggleButton(tool.name, tool.iconType))}
    </ToggleButtonGroup>;
  }

  private renderVertical() {
    if (this.props.floating) {
      return <Box style={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        alignItems: "flex-start",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 1200,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}>
        {this.renderVerticalInternal()}
      </Box>;
    } else {
      return this.renderVerticalInternal();
    }
  }

  private renderVerticalInternal() {
    return <ToggleButtonGroup
      orientation="vertical"
      value={this.state.selectedTool}
      exclusive
      onChange={(e, value) => {
        this.setState({
          selectedTool: value,
        });
        this.props.onSelectedToolChanged(value);
      }}
      style={{margin: PD_SM, width: SZ_SM, pointerEvents: "auto"}}
    >
      {this.props.tools.filter(tool => !tool.hidden).map(tool => this.createToggleButton(tool.name, tool.iconType))}
    </ToggleButtonGroup>;
  }

  private createToggleButton(value: string, iconType: typeof SvgIcon): ReactElement {
    const theme = BaseApp.CONTEXT.getAppConfig().theme;
    let IconType = iconType;
    return <ToggleButton
      style={{
        backgroundColor: this.state.selectedTool === value ? theme.palette.primary.main : white,
        width: 48,
        height: 48
      }}
      value={value}>
      <IconType style={this.state.selectedTool === value ? ICON_BUTTON_LIGHT_XSM_STYLE : ICON_BUTTON_XSM_STYLE_PD}/>
    </ToggleButton>;
  }
}

export type StyledDashboardWidgetRenderer = {
  title?: string,
  rows: number,
  columns: number,
  render: (width: number, height: number) => ReactElement,
}

export type StyledDashboardWidgetProps = {
  cellSize: number,
  renderer: StyledDashboardWidgetRenderer,
}

type StyledDashboardWidgetState = {}

export class StyledDashboardWidget extends React.Component<StyledDashboardWidgetProps, StyledDashboardWidgetState> {

  render(): ReactElement {
    const width = this.props.cellSize * this.props.renderer.columns;
    const height = this.props.cellSize * this.props.renderer.rows;
    return <Box style={{
      display: "flex",
      flexDirection: "column",
      margin: PD_MD,
      padding: PD_SM,
      borderRadius: BORDER_RADIUS,
      border: DIVIDER,
      background: white
    }}>
      {this.props.renderer.title ?
        <Typography style={{fontWeight: "bold", padding: PD_SM}}>{this.props.renderer.title}</Typography>
        : null}
      <Box style={{width: width, height: height}}>
        {this.props.renderer.render(width, height)}
      </Box>
    </Box>;
  }
}

export type StyledDashboardWidgetContainerProps = {
  renderers: StyledDashboardWidgetRenderer[],
}

export class StyledDashboardWidgetContainer extends React.PureComponent<StyledDashboardWidgetContainerProps> {

  render(): ReactElement {
    return <StyledBoxRow style={{flexGrow: 1, alignItems: "flex-start"}}>
      {this.props.renderers.map(renderer => <StyledDashboardWidget cellSize={72} renderer={renderer}/>)}
    </StyledBoxRow>
  }
}

export function StyledMarkdown(props: { style?: any, children: any, options?: Options }) {
  return <Typography component="div" style={{fontSize: "100%", ...props.style}}>
    <Markdown
      components={{
        img: (imageData) => {
          // const size = imageData.alt?.match(/\{(\d+)x(\d+)}/);
          // const width = size ? size[1] : null;
          // const height = size ? size[2] : null;
          return <img src={imageData.src} style={{maxWidth: 320, maxHeight: 320, objectFit: "contain"}}/>
        },
      }}
      {...props.options}>
      {props.children}
    </Markdown>
  </Typography>;
}